/**
 * React Query mutations for answer operations
 * Handles answer submission and approval workflows
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { lessonsAPI } from '../api/lessons.api';
import { lessonQueryKeys } from './useLessonsQueries';
import { api } from '../../../core/api/queries';
import type { AnswerDTO } from '../core/types/lesson.types';
import { getErrorMessage, getErrorStatus } from '@/core/api/response-handler';
import { trebuieToastGeneric } from '../utils/toast-trimitere';

/**
 * Hook for managing answer mutations
 * Follows the same pattern as lesson mutations for consistency
 *
 * @example
 * const mutations = useAnswerMutations();
 * mutations.submit.mutate(answer);
 * mutations.approve.mutate({ id: 'answer-123', isApproved: true });
 */
export function useAnswerMutations() {
  const queryClient = useQueryClient();

  /**
   * Mutation for submitting an answer
   * Used by students to submit their answers to slides
   */
  const submit = useMutation({
    mutationFn: async (answer: AnswerDTO) => {
      return lessonsAPI.submitAnswer(answer);
    },
    onSuccess: (data, _) => {
      // Don't show toast for every answer - too spammy
      // Components can show their own success messages

      // ✅ PATTERN: Discriminated union check for ephemeral mode
      // Both must be null to indicate ephemeral mode (practice/retry without save)
      const isEphemeral = data.punctajFinal === null && data.isLessonCompletedNow === null;

      if (isEphemeral) {
        // Ephemeral mode - no database changes occurred
        // Skip cache invalidation (TanStack Query optimization)
        return;
      }

      // Normal mode - invalidate caches to refetch updated data
      if (data.lessonId) {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.answersForLesson(data.lessonId),
        });
      }
      if (data.lessonId && data.userId) {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.answersForUser(
            data.lessonId,
            data.userId
          ),
        });
      }
      if (data.isLessonCompletedNow === true) {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.lists(),
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(error, '');
      const statusCode = getErrorStatus(error);

      // ✅ PRIMARY: Handle 409 Conflict (duplicate answer from backend)
      if (statusCode === 409) {
        toast.warning(
          errorMessage || 'Această întrebare a fost deja răspunsă.',
          {
            autoClose: 3000,
            toastId: 'duplicate-answer-409'
          }
        );

        // Force refetch to sync frontend state with backend
        if (variables?.lessonId) {
          queryClient.invalidateQueries({
            queryKey: lessonQueryKeys.answersForLesson(variables.lessonId)
          });
        }
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.lists()
        });

        return; // Don't show additional error
      }

      // ✅ FALLBACK: Handle 400 errors (backwards compatibility for old backend)
      if (statusCode === 400) {
        // Validate this is actually a duplicate/constraint error (not other 400 errors)
        const isDuplicateError =
          errorMessage.includes('Constrangerile restrictioneaza') ||
          errorMessage.includes('duplicate key') ||
          errorMessage.includes('DataIntegrityViolationException') ||
          errorMessage.toLowerCase().includes('unique constraint') ||
          errorMessage.includes('Ai început deja această lecție');

        if (isDuplicateError) {
          toast.warning(
            'Întrebare deja răspunsă. Reîncărcăm lecția...',
            {
              autoClose: 2000,
              toastId: 'duplicate-reload'
            }
          );

          // Force page reload to resync state
          setTimeout(() => window.location.reload(), 2000);
          return;
        }
        // If not a duplicate error, fall through to other error handlers
      }

      // Check for pending approval block
      if (errorMessage.includes('in curs de aprobare') ||
          errorMessage.toLowerCase().includes('pending approval')) {
        toast.warning(
          '⏳ Nu poți relua această lecție. Așteaptă aprobarea profesorului.',
          {
            autoClose: 5000,
            toastId: 'pending-approval-block' // Prevent duplicate toasts
          }
        );
        return;
      }

      // 🔴 SP-05a, valul (d), R1: cand serverul a numit fragmentele, ele sunt DEJA pe ecran,
      //    langa camp, cu diacritice si cu instructiune. Un toast rosu peste ele inseamna doua
      //    mesaje deodata pentru aceeasi eroare, al doilea in forma bruta refuzata la #113.
      //    Masurat de S1 la 400, pe `51baf56d`.
      //    ⚠️ Se tace INGUST, nu pentru toata lumea: decizia sta in `trebuieToastGeneric`, cu
      //    control negativ in teste. Fara fragmente, toastul asta e singurul mesaj pe care il
      //    primeste omul — scos de tot, o trimitere esuata ar arata ca una neplecata.
      if (!trebuieToastGeneric(error)) {
        return;
      }

      // Default error handling
      toast.error(errorMessage || 'Eroare la salvarea răspunsului');
    },
  });

  /**
   * Mutation for batch submitting multiple answers
   * Used when completing an entire lesson at once
   */
  const submitBatch = useMutation({
    mutationFn: async (answers: AnswerDTO[]) => {
      // Submit all answers in parallel
      const promises = answers.map((answer) => lessonsAPI.submitAnswer(answer));
      return Promise.all(promises);
    },
    onSuccess: (data) => {
      toast.success('Toate răspunsurile au fost salvate cu succes');

      // Invalidate all answer queries for affected lessons
      const uniqueLessonIds = new Set(data.map((answer) => answer.lessonId).filter(Boolean));
      uniqueLessonIds.forEach((lessonId) => {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.answersForLesson(lessonId!),
        });
      });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la salvarea răspunsurilor')
      );
    },
  });

  /**
   * Mutation for approving an answer
   * Used by admins/teachers for homework and practical activities
   */
  const approve = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      return lessonsAPI.updateApproval(id, true, reason);
    },
    onSuccess: () => {
      toast.success('Răspunsul a fost aprobat cu succes');
      // Invalidate reports that show approval status
      queryClient.invalidateQueries({ queryKey: api.lessons.answers.report.getKey() });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la aprobarea răspunsului')
      );
    },
  });

  /**
   * Mutation for rejecting an answer
   * Requires a reason for rejection
   */
  const reject = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      if (!reason || reason.trim().length === 0) {
        throw new Error('Motivul respingerii este obligatoriu');
      }
      return lessonsAPI.updateApproval(id, false, reason);
    },
    onSuccess: (response) => {
      // ✅ PATTERN: Handle both null and object responses (discriminated union)
      if (response === null || response === undefined) {
        // Backend deleted the attempt - student can retry
        toast.success(
          '↩️ Răspuns respins. Elevul poate trimite o nouă încercare.',
          {
            autoClose: 5000,
            toastId: 'answer-rejected'
          }
        );

        // ✅ CRITICAL: Invalidate lesson lists to update attemptState
        // When professor rejects, backend deletes the attempt so student can retry with points
        queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
      } else {
        // Fallback (shouldn't happen with new backend)
        toast.success('Răspunsul a fost respins');
      }

      // Always invalidate reports
      queryClient.invalidateQueries({ queryKey: api.lessons.answers.report.getKey() });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la respingerea răspunsului')
      );
    },
  });

  /**
   * Mutation for batch approval of multiple answers
   * Used for bulk operations in admin panels
   */
  const approveBatch = useMutation({
    mutationFn: async (ids: string[]) => {
      const promises = ids.map((id) => lessonsAPI.updateApproval(id, true));
      return Promise.all(promises);
    },
    onSuccess: (_, variables) => {
      toast.success(`${variables.length} răspunsuri au fost aprobate`);
      queryClient.invalidateQueries({ queryKey: api.lessons.answers.report.getKey() });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la aprobarea răspunsurilor')
      );
    },
  });

  /**
   * Optimistic answer submission
   * Updates UI immediately before server confirms
   */
  const submitOptimistic = useMutation({
    mutationFn: async (answer: AnswerDTO) => {
      return lessonsAPI.submitAnswer(answer);
    },
    onMutate: async (newAnswer) => {
      if (!newAnswer.lessonId || !newAnswer.userId) {
        return;
      }

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: lessonQueryKeys.answersForUser(
          newAnswer.lessonId,
          newAnswer.userId),
      });

      // Snapshot previous value
      const previousAnswers = queryClient.getQueryData<AnswerDTO[]>(
        lessonQueryKeys.answersForUser(
          newAnswer.lessonId,
          newAnswer.userId)
      );

      // Optimistically update cache
      if (previousAnswers) {
        const updatedAnswers = [...previousAnswers, newAnswer];
        queryClient.setQueryData(
          lessonQueryKeys.answersForUser(
            newAnswer.lessonId,
            newAnswer.userId),
          updatedAnswers
        );
      }

      return { previousAnswers };
    },
    onError: (error, newAnswer, context) => {
      // Rollback on error
      if (context?.previousAnswers && newAnswer.lessonId && newAnswer.userId) {
        queryClient.setQueryData(
          lessonQueryKeys.answersForUser(
            newAnswer.lessonId,
            newAnswer.userId),
          context.previousAnswers
        );
      }
      toast.error(
        getErrorMessage(error, 'Eroare la salvarea răspunsului')
      );
    },
    onSettled: (data) => {
      // Refetch after success or error
      if (data?.lessonId && data?.userId) {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.answersForUser(
            data.lessonId,
            data.userId),
        });
      }
    },
  });

  return {
    // Mutations
    submit,
    submitBatch,
    approve,
    reject,
    approveBatch,
    submitOptimistic,

    // Loading states
    isSubmitting: submit.isPending,
    isSubmittingBatch: submitBatch.isPending,
    isApproving: approve.isPending,
    isRejecting: reject.isPending,
    isApprovingBatch: approveBatch.isPending,

    // General loading state
    isLoading:
      submit.isPending ||
      submitBatch.isPending ||
      approve.isPending ||
      reject.isPending ||
      approveBatch.isPending,

    // Error states
    submitError: submit.error,
    approveError: approve.error,
    rejectError: reject.error,
  };
}

